/**
 * @ Author: Housefolios
 * @ Create Time: 2021-11-22 08:46:15
 * @ Modified by: David Helmick
 * @ Modified time: 2025-10-23 14:19:27
 * @ Description: Dialog for the creation of a new portfolio
 */

import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Grid,
  Dialog,
  Typography,
  Card,
  Button,
  Link,
  List,
  ListItem,
  ListItemButton,
  MenuItem,
  TextField,
  DialogTitle,
  DialogContent,
  FormControl,
  Box,
} from '@mui/material'

import Autocomplete from '@mui/material/Autocomplete'

// Centralized lodash imports - much cleaner!
import {
  merge,
  cloneDeep,
  includes,
  isEmpty,
  filter,
  get,
  map,
  head,
} from '@/utils/lodash'

import hero1 from '@/assets/images/hero-bg/hero-1.jpg'
import { enqueueSnackbar } from 'notistack'
import withSession from '@/housefolios-components/Session/withSession'
import { useMutation, useQuery } from '@apollo/client/react'
import { ADD_PORTFOLIO } from '../mutations'
import { GET_ME } from '@/housefolios-components/Session/queries'
import { GET_PORTFOLIOS, GET_SIDEBAR_PORTFOLIOS } from '../queries'
import { launchpadSubtitleSx } from '@/housefolios-components/shared'
import { Add } from '@mui/icons-material'
import BlockUi from 'react-block-ui'
import { PacmanLoader } from 'react-spinners'
import { DEFAULT_PREFERENCES } from '@/housefolios-components/OrganizationSettings/OrganizationPreferences'
import {
  TextValidator,
  ValidatorForm,
} from '@/housefolios-components/shared/muiFormValidator'
import {
  filterPreferedStrategies,
  getStrategyName,
} from '@/utils/stratagies'

function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <Typography
      component="div"
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && <div>{children}</div>}
    </Typography>
  )
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
}

function CreatePortfolioComponent(props) {
  const { headerDot, launchpad, sidebarMenu, gridButton } = props

  const [openCreateNewPortfolio, setCreateNewPortfolio] =
    useState(false)

  const [loader, setLoader] = useState(false)
  const { limits, activeOrg } = props.session.me
  const { portfolioLimit } = limits

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [strategy, setStrategy] = useState(
    merge(
      cloneDeep(DEFAULT_PREFERENCES),
      props?.session?.me?.activeOrg?.memberPreferences,
      props?.session?.me?.activeOrg?.preferences,
    ).general['defaultStrategy'],
  )
  const [assumptions, setAssumptions] = useState({})

  const {
    _id: RemovedId,
    __typename: RemovedTypename,
    ...defaultAssumptions
  } = activeOrg.assumptions

  const [preferences, setPreferences] = useState(
    merge(
      cloneDeep(DEFAULT_PREFERENCES),
      props?.session?.me?.activeOrg?.memberPreferences,
      props?.session?.me?.activeOrg?.preferences,
    ),
  )

  // Never setState during render — empty preferred lists loop forever.
  useEffect(() => {
    const preferred = filterPreferedStrategies(
      preferences,
      props?.session?.me?.activeOrg?.member,
      true,
    )
    if (!isEmpty(preferred) && !includes(preferred, strategy)) {
      setStrategy(head(preferred))
    }
  }, [preferences, strategy, props?.session?.me?.activeOrg?.member])

  const handleChangeDropdown = (event, value, collection, name) => {
    let newPreferences = { ...preferences }
    let newValue = value
    if (name === 'strategies') {
      newPreferences[collection][name] = map(
        newPreferences[collection][name],
        (preference) => {
          return {
            value: preference.value,
            active: includes(newValue, preference.value),
            memberOnly: preference.memberOnly,
            memberExcluded: preference.memberExcluded,
          }
        },
      )
    } else newPreferences[collection][name] = newValue
    setPreferences(newPreferences)
  }

  const handleOpenCreateNewPortfolio = () => {
    if (props.closeMenu) props.closeMenu()
    setCreateNewPortfolio(true)
    setName('')
    setDescription('')
    setStrategy(preferences.general['defaultStrategy'])
    setAssumptions(defaultAssumptions)
    setLoader(false)
    setPreferences(
      merge(
        cloneDeep(DEFAULT_PREFERENCES),
        props?.session?.me?.activeOrg?.memberPreferences,
        props?.session?.me?.activeOrg?.preferences,
      ),
    )
  }
  const handleCloseCreateNewPortfolio = () => {
    setCreateNewPortfolio(false)
    setName('')
    setDescription('')
    setStrategy(preferences.general['defaultStrategy'])
    setAssumptions(defaultAssumptions)
  }

  const [addPortfolio] = useMutation(ADD_PORTFOLIO)

  const [activeTab, setActiveTab] = useState('0')

  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab)
  }

  const { error, data, loading } = useQuery(GET_PORTFOLIOS)
  // if (loading) return 'Loading...'
  if (error) return `Error! ${error.message}`
  const { portfolios = [] } = data || {}

  return (
    <>
      {sidebarMenu && (
        <>
          <ListItemButton
            component={Link}
            style={{
              textAlign: 'center',
              color: '#04a3e3',
              marginLeft: 8,
            }}
            onClick={
              portfolios.length >= portfolioLimit
                ? () => props.toggleLimit('portfolio')
                : handleOpenCreateNewPortfolio
            }
            disabled={loading}
          >
            <Add style={{ marginRight: 10 }} />
            Add Portfolio
          </ListItemButton>
        </>
      )}
      {props.dropdown && (
        <ListItemButton
          style={{
            textAlign: 'center',
            color: '#04a3e3',
            marginLeft: 8,
          }}
          onClick={
            portfolios.length >= portfolioLimit
              ? () => props.toggleLimit('portfolio')
              : handleOpenCreateNewPortfolio
          }
          disabled={loading}
        >
          <Add style={{ marginRight: 10 }} />
          Add Portfolio
        </ListItemButton>
      )}
      {headerDot && (
        <Grid size={{ sm: 6 }}>
          <Button
            onClick={
              portfolios.length >= portfolioLimit
                ? () => props.toggleLimit('portfolio')
                : handleOpenCreateNewPortfolio
            }
            variant="text"
            disabled={props.disabled || loading}
           
            sx={{
              backgroundColor: "transparent",
              color: "#f8f9ff",
              border: 0,
              "&:not(:disabled):hover": { boxShadow: "0 0.22rem 0.525rem rgba(248, 249, 255, 0.4), 0 0.0625rem 0.385rem rgba(248, 249, 255, 0.54)", backgroundColor: "#f8f9ff", color: "#3b3e66" },
              "&:not(:disabled).active, &:hover": { backgroundColor: "#f8f9ff", color: "#3b3e66" },
              "html[data-color-mode=\"dark\"] &:not(:disabled):hover, html[data-color-mode=\"dark\"] &:not(:disabled).active, html[data-color-mode=\"dark\"] &:hover, html[data-color-mode=\"dark\"] &.active": { backgroundColor: "#2a2a2a !important", color: "rgba(255, 255, 255, 0.87) !important", boxShadow: "none" },
              "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87)" },
              "&:hover": { transform: "translateY(0px)" },
              width: "100%",
              display: "block",
              borderRadius: 0,
              boxShadow: "none",
              py: "1.5rem",
              px: "0.25rem",
            }}
          >
            <FontAwesomeIcon
              icon={['fas', 'folder-plus']}
              style={{ color: props.color || '#28a745', fontSize: '2rem' }}
              sx={{
                display: 'block',
                mb: '0.5rem',
                mx: 'auto',
                mt: '0.25rem',
              }}
            />
            <Box sx={[{ fontWeight: 700 }, {
              color: "#070919",
              "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
            }]}>
              Create Portfolio
            </Box>
            <Box data-hf="muted" sx={launchpadSubtitleSx}>
              Keep it organized
            </Box>
          </Button>
        </Grid>
      )}
      {gridButton && (
        <Grid size={3}>
          <Button
            variant="outlined"
            onClick={
              portfolios.length >= portfolioLimit
                ? () => props.toggleLimit('portfolio')
                : handleOpenCreateNewPortfolio
            }
            fullWidth
          >
            New Portfolio
          </Button>
        </Grid>
      )}
      {launchpad && (
        <Button
          variant="text"
         
          disabled={loading}
          onClick={
            portfolios.length >= portfolioLimit
              ? () => props.toggleLimit('portfolio')
              : handleOpenCreateNewPortfolio
          }
          sx={{
            border: 0,
            width: '100%',
            display: 'block',
            borderRadius: 0,
            boxShadow: 'none',
            py: '1.5rem',
            px: '0.25rem',
          }}
        >
          <FontAwesomeIcon
            icon={['fas', 'folder-plus']}
            style={{ color: props.color || '#28a745', fontSize: '2rem' }}
            sx={{
              display: 'block',
              mx: 'auto',
              mb: '0.5rem',
              mt: '0.25rem',
            }}
          />
          <Box sx={[{ fontWeight: 700 }, {
            fontSize: "1rem !important",
            color: "#070919",
            "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
          }]}>
            Create Portfolio
          </Box>
          <Box data-hf="muted" sx={launchpadSubtitleSx}>
            Keep it organized
          </Box>
        </Button>
      )}
      <Dialog
        open={openCreateNewPortfolio}
        onClose={handleCloseCreateNewPortfolio}
        maxWidth="md"
        fullWidth
        onKeyDown={(event) => event.stopPropagation()}
        slotProps={{
          paper: {
            className: 'modal-content',
            sx: {
              borderRadius: '0.65rem',
              border: 0,
              backgroundColor: '#fff',
              padding: '1rem',
              '@media (min-width: 1440px)': { padding: 0 },
            },
          },
        }}
      >
        <BlockUi
          blocking={loader}
          loader={
            <PacmanLoader color={'var(--primary)'} loading={true} />
          }
          message={
            <Box sx={{ color: '#368cff' }}>Creating Portfolio</Box>
          }
        >
          <ValidatorForm
            id="addPortfolio"
            onSubmit={(event) => {
              setLoader(true)
              addPortfolio({
                awaitRefetchQueries: true,
                variables: {
                  portfolioInput: {
                    name,
                    strategy,
                    strategies: get(
                      preferences,
                      'general.strategies',
                    ),
                    description,
                    assumptions,
                  },
                },
                refetchQueries: [
                  {
                    query: GET_PORTFOLIOS,
                  },
                  {
                    query: GET_SIDEBAR_PORTFOLIOS,
                  },
                  {
                    query: GET_ME,
                  },
                ],
              }).then(({ data  }) => {
                enqueueSnackbar(`Portfolio Created`, {
                  variant: 'success',
                  autoHideDuration: 3000,
                })
                if (props.setNewPortfolio)
                  props.setNewPortfolio(data.addPortfolio._id)
                handleCloseCreateNewPortfolio()
              })
            }}
          >
            <DialogTitle>Create New Portfolio</DialogTitle>
            <DialogContent>
              <Box sx={{
                "@media (min-width: 1440px)": { mt: 0 },
                mt: "1.5rem",
              }}>
                {/* Section Header for Portfolio Details */}
                <Box
                  sx={{
                    background: '#eaf4ff',
                    p: '16px 24px',
                    borderRadius: 1,
                    mt: 3,
                    'html[data-color-mode="dark"] &': {
                      background: '#2a2a2a !important',
                    },
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#183153',
                      m: 0,
                      'html[data-color-mode="dark"] &': {
                        color: 'rgba(255, 255, 255, 0.87) !important',
                      },
                    }}
                  >
                    Portfolio Details
                  </Typography>
                </Box>

                <div>
                  <Card
                    sx={{
                      boxShadow: 'none',
                      backgroundColor: 'transparent',
                      p: '1.5rem',
                      border: 0,
                    }}
                  >
                    <div>
                      <Box sx={{ mb: '1rem' }}>
                        <TextValidator
                          variant="outlined"
                          margin="dense"
                          fullWidth={true}
                          required={true}
                          autoFocus
                          value={name}
                          onChange={(event) =>
                            setName(event.target.value)
                          }
                          label="Portfolio Name"
                          validators={[
                            'required',
                            'maxStringLength: 50',
                          ]}
                          errorMessages={[
                            'This field is required',
                            'You cannot exceed 50 characters',
                          ]}
                        />
                      </Box>
                      <Box sx={{ mb: '1rem' }}>
                        <TextField
                          variant="outlined"
                          margin="dense"
                          label="Portfolio Description"
                          fullWidth={true}
                          value={description}
                          onChange={(event) =>
                            setDescription(event.target.value)
                          }
                        />
                      </Box>
                      <Box sx={{ mb: '1rem' }}>
                        <Autocomplete
                          id="checkboxes-tags-demo"
                          multiple
                          disableCloseOnSelect
                          options={filterPreferedStrategies(
                            preferences,
                            props?.session?.me?.activeOrg?.member,
                            false,
                          )}
                          value={filterPreferedStrategies(
                            preferences,
                            props?.session?.me?.activeOrg?.member,
                            true,
                          )}
                          onChange={(event, newValue) => {
                            if (!isEmpty(newValue))
                              handleChangeDropdown(
                                event,
                                newValue,
                                'general',
                                'strategies',
                              )
                          }}
                          getOptionLabel={(option) =>
                            getStrategyName(
                              option,
                              props.session.me.activeOrg.member,
                            )
                          }
                          disableClearable
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              label="Strategies"
                            />
                          )}
                        />
                      </Box>
                      <Box sx={{ mb: '1rem' }}>
                        <TextField
                          select
                          variant="outlined"
                          margin="dense"
                          label="Default Property Strategy"
                          fullWidth={true}
                          value={strategy}
                          onChange={(event) =>
                            setStrategy(event.target.value)
                          }
                          required={true}
                        >
                          {map(
                            filter(
                              preferences.general['strategies'],
                              'active',
                            ),
                            (preference) => (
                              <MenuItem
                                key={preference.value}
                                value={preference.value}
                              >
                                {getStrategyName(
                                  preference.value,
                                  props.session.me.activeOrg.member,
                                )}
                              </MenuItem>
                            ),
                          )}
                        </TextField>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Button
                          variant="contained"

                          color="primary"
                          style={{ color: 'gray' }}
                          disabled={loader || !name || !strategy}
                          type="submit"
                          form="addPortfolio"
                          sx={{
                            fontWeight: 700,
                            px: '1.5rem',
                            my: '1rem',
                          }}
                        >
                          Create Portfolio
                        </Button>
                      </Box>
                    </div>
                  </Card>
                </div>
              </Box>
            </DialogContent>
          </ValidatorForm>
        </BlockUi>
      </Dialog>
    </>
  )
}

export default withSession(CreatePortfolioComponent)
